/**
 * Subscription Widget
 * Creates a custom subscription widget for product pages
 */

class SubscriptionWidget {
  constructor() {
    this.targetElement = null;
    this.originalAppElement = null;
    this.currentProductId = null;
    this.currentVariantId = null;
    this.selectedInterval = '30-day';
    this.selectedQuantity = 2; // Default to first available option (2, 3, 4)
    this.selectedOption = 'one-time'; // 'one-time' or 'recurring' - default to one-time
    this.relatedProducts = [];
    this.relatedProductsData = {};
    this.productQuantities = {};
    this.observer = null;
    this.shopName = '';
    this.init();
  }

  init() {
    // Check if we're on a product page with subscription target
    if (!this.hasSubscriptionTarget()) {
      return;
    }

    // Get current product data
    this.getCurrentProductData();

    // Setup MutationObserver to watch for app element
    this.setupObserver();

    // Try to initialize immediately in case element already exists
    this.checkAndInitialize();

    // Also try after a short delay to catch late-loading elements
    setTimeout(() => {
      this.checkAndInitialize();
    }, 500);

    // And try again after a longer delay
    setTimeout(() => {
      this.checkAndInitialize();
    }, 1500);
  }

  hasSubscriptionData() {
    // Check if product has subscription-related metafields
    const productJson = document.querySelector('[data-product-json]');
    if (!productJson) {
      return false;
    }

    try {
      const productData = JSON.parse(productJson.textContent);
      return !!(productData.metafields?.custom?.produtos_relacionados_de_assinatura || 
                productData.metafields?.custom?.quantidade_assinatura);
    } catch (e) {
      return false;
    }
  }

  hasSubscriptionTarget() {
    // Check if target element exists or if select exists (app might have loaded)
    this.targetElement = document.querySelector('.sealsubs-target-element');
    const appSelect = document.querySelector('select.sls-select.seal-tight[name="subs_interval"]');
    return !!(this.targetElement || appSelect);
  }

  getCurrentProductData() {
    // Get product ID from meta tag or data attribute
    const productMeta = document.querySelector('meta[property="product:retailer_item_id"]');
    if (productMeta) {
      this.currentProductId = productMeta.getAttribute('content');
    }

    // Get current variant ID from form
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      this.currentVariantId = variantInput.value;
    }

    // Get product JSON data
    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      try {
        const productData = JSON.parse(productJson.textContent);
        this.currentProductId = productData.id;
        this.currentVariantId = productData.selected_or_first_available_variant?.id;
        
        // Get related products from metafield
        if (productData.metafields?.custom?.produtos_relacionados_de_assinatura) {
          const relatedProductsData = productData.metafields.custom.produtos_relacionados_de_assinatura;
          // Handle both array and single product reference
          if (Array.isArray(relatedProductsData)) {
            this.relatedProducts = relatedProductsData;
            
            // Process related products and extract quantities
            relatedProductsData.forEach(relatedProduct => {
              if (relatedProduct.id) {
                // Store product data
                if (!this.relatedProductsData) {
                  this.relatedProductsData = {};
                }
                this.relatedProductsData[relatedProduct.id] = relatedProduct;
                
                // Extract quantity from metafield if available
                if (relatedProduct.metafields?.custom?.quantidade_assinatura) {
                  const qty = parseInt(relatedProduct.metafields.custom.quantidade_assinatura);
                  if (qty && !isNaN(qty)) {
                    this.productQuantities[relatedProduct.id] = qty;
                  }
                }
              }
            });
          } else if (relatedProductsData) {
            this.relatedProducts = [relatedProductsData];
            // Process single product
            if (relatedProductsData.id) {
              if (!this.relatedProductsData) {
                this.relatedProductsData = {};
              }
              this.relatedProductsData[relatedProductsData.id] = relatedProductsData;
              
              if (relatedProductsData.metafields?.custom?.quantidade_assinatura) {
                const qty = parseInt(relatedProductsData.metafields.custom.quantidade_assinatura);
                if (qty && !isNaN(qty)) {
                  this.productQuantities[relatedProductsData.id] = qty;
                }
              }
            }
          }
        }

        // Get current product quantity
        if (productData.metafields?.custom?.quantidade_assinatura) {
          const qty = parseInt(productData.metafields.custom.quantidade_assinatura);
          if (qty && !isNaN(qty)) {
            this.productQuantities[productData.id] = qty;
            // Only set if it's one of the allowed quantities (2, 3, 4)
            if (qty === 2 || qty === 3 || qty === 4) {
              this.selectedQuantity = qty;
            } else {
              this.selectedQuantity = 2; // Default to 2 if metafield has invalid value
            }
          }
        }

        // Get shop name
        if (productData.shopName) {
          this.shopName = productData.shopName;
        }
      } catch (e) {
      }
    }
  }

  setupObserver() {
    this.observer = new MutationObserver((mutations) => {
      this.checkAndInitialize();
    });

    // Observe the document body for changes to catch app elements being added
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also observe the target element's parent if it exists
    if (this.targetElement && this.targetElement.parentElement) {
      const parentObserver = new MutationObserver((mutations) => {
        this.checkAndInitialize();
      });
      parentObserver.observe(this.targetElement.parentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  checkAndInitialize() {
    // Check if app element exists - try multiple selectors
    let appElement = document.querySelector('select.sls-select.seal-tight[name="subs_interval"]');
    
    // If select not found, try to find by container
    if (!appElement) {
      const appContainer = document.querySelector('.sealsubs-target-element');
      if (appContainer) {
        appElement = appContainer.querySelector('select[name="subs_interval"]');
      }
    }
    
    if (appElement) {
      // Find the original app element container
      const appContainer = appElement.closest('.sealsubs-target-element');
      
      if (appContainer && appContainer !== this.originalAppElement) {
        this.originalAppElement = appContainer;

        // Hide original element visually but keep it functional
        this.hideOriginalElementVisually();

        // Setup observer for app widget changes
        this.setupAppWidgetObserver();

        // Create widget if it doesn't exist
        if (!document.querySelector('.subscription-widget')) {
          this.createWidget();
        }
      } else if (appContainer && appContainer === this.originalAppElement) {
        // Element already set, but widget might not exist yet
        if (!document.querySelector('.subscription-widget')) {
          this.createWidget();
        }
      }
    } else {
      // Try to find container directly even without select
      const appContainer = document.querySelector('.sealsubs-target-element');
      if (appContainer && appContainer !== this.originalAppElement) {
        // Check if it has subscription options
        const hasSubscriptionOptions = appContainer.querySelector('input[name^="subs_type_"][value="subscription"]');
        if (hasSubscriptionOptions) {
          this.originalAppElement = appContainer;

          // Hide original element visually but keep it functional
          this.hideOriginalElementVisually();

          // Setup observer for app widget changes
          this.setupAppWidgetObserver();

          // Create widget if it doesn't exist
          if (!document.querySelector('.subscription-widget')) {
            this.createWidget();
          }
        }
      }
    }
  }

  hideOriginalElementVisually() {
    if (!this.originalAppElement) return;
    
    // Hide visually but keep functional
    this.originalAppElement.style.position = 'absolute';
    this.originalAppElement.style.width = '1px';
    this.originalAppElement.style.height = '1px';
    this.originalAppElement.style.opacity = '0';
    this.originalAppElement.style.overflow = 'hidden';
    this.originalAppElement.style.pointerEvents = 'none';
    this.originalAppElement.setAttribute('aria-hidden', 'true');
  }

  setupAppWidgetObserver() {
    if (!this.originalAppElement) return;

    // Flag to prevent infinite loops
    this.isUpdatingAppWidget = false;

    // Observe changes to hidden inputs and select
    const observer = new MutationObserver((mutations) => {
      // When app updates values, update price display if discount changed
      let discountChanged = false;
      let newInputsAdded = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const target = mutation.target;
          if (target.classList.contains('sls-input-discount-value') ||
              target.classList.contains('sls-input-discount-value-visible')) {
            discountChanged = true;
          }
          if (target.classList.contains('sls-input-interval') || 
              target.classList.contains('sls-input-id') ||
              target.classList.contains('sls-input-discount-value-hash')) {
            // Interval or discount hash changed, update price
            discountChanged = true;
          }
        }
        
        // Check if new inputs were added
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && node.tagName === 'INPUT' && node.name && node.name.startsWith('properties[')) {
              const match = node.name.match(/properties\[(.+)\]/);
              if (match) {
                const key = match[1];
                const isSubscriptionProperty = key.startsWith('_sealsub_') || 
                                               key === 'Intervalo de assinatura' || 
                                               key === 'Desconto no próximo pedido' ||
                                               key.includes('Intervalo') ||
                                               key.includes('Desconto') ||
                                               key.includes('sealsub') ||
                                               key.toLowerCase().includes('subscription') ||
                                               key.toLowerCase().includes('assinatura');
                
                if (isSubscriptionProperty) {
                  newInputsAdded = true;
                }
              }
            }
          });
        }
      });
      
      // If new subscription inputs were added
      if (newInputsAdded) {
        // Properties detected
      }
      
      // Update price display if discount changed
      if (discountChanged && !this.isUpdatingAppWidget) {
        const widget = document.querySelector('.subscription-widget');
        if (widget) {
          setTimeout(() => {
            this.updatePriceDisplay(widget);
          }, 100);
        }
      }
      
      // Note: We no longer sync quantity from form to avoid conflicts with bundles app
      // The subscription widget manages its own quantity (2, 3, 4)
    });

    // Observe the app element for attribute changes and child additions
    observer.observe(this.originalAppElement, {
      attributes: true,
      attributeFilter: ['value'],
      subtree: true,
      childList: true
    });
    
    // Also observe the form for new inputs being added
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      const formObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1 && node.tagName === 'INPUT' && node.name && node.name.startsWith('properties[')) {
                // Property input added to form
              }
            });
          }
        });
      });
      
      formObserver.observe(form, {
        childList: true,
        subtree: true
      });
    }

    // Listen to radio button changes (but not select to avoid loops)
    const radios = this.originalAppElement.querySelectorAll('input[name^="subs_type_"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        // Only sync if we're not the ones updating
        if (this.isUpdatingAppWidget) return;

        const widget = document.querySelector('.subscription-widget');
        if (!widget) return;
        const oneTimeOption = widget.querySelector('.subscription-widget__option[data-option="one-time"]');
        const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
        if (!oneTimeOption || !recurringOption) return;

        // Sempre deixar só uma opção selecionada: limpar todas e marcar a que o app indica
        widget.querySelectorAll('.subscription-widget__option').forEach(function(opt) {
          opt.classList.remove('subscription-widget__option--selected');
        });
        widget.querySelectorAll('.subscription-widget__radio').forEach(function(r) {
          r.classList.remove('subscription-widget__radio--selected');
        });

        if (radio.value === 'one_time') {
          oneTimeOption.classList.add('subscription-widget__option--selected');
          var r = oneTimeOption.querySelector('.subscription-widget__radio');
          if (r) r.classList.add('subscription-widget__radio--selected');
          this.selectedOption = 'one-time';
          document.body.classList.remove('subscription-recurring-selected');
        } else if (radio.value === 'subscription') {
          recurringOption.classList.add('subscription-widget__option--selected');
          var r2 = recurringOption.querySelector('.subscription-widget__radio');
          if (r2) r2.classList.add('subscription-widget__radio--selected');
          this.selectedOption = 'recurring';
          document.body.classList.add('subscription-recurring-selected');
        }
      });
    });
  }

  createWidget() {
    // Check if widget already exists
    if (document.querySelector('.subscription-widget')) {
      return;
    }

    // Create widget container
    const widget = document.createElement('div');
    widget.className = 'subscription-widget';
    widget.innerHTML = this.getWidgetHTML();

    // Always insert immediately before the add to cart button
    const addToCartButton = this.findAddToCartButton();
    if (addToCartButton) {
      // Find the parent container of the button (could be a wrapper div)
      const buttonWrapper = addToCartButton.closest('.product-form__buttons') || addToCartButton.parentElement;
      if (buttonWrapper) {
        buttonWrapper.parentElement.insertBefore(widget, buttonWrapper);
      } else {
        addToCartButton.parentElement.insertBefore(widget, addToCartButton);
      }
    } else {
      // Fallback: try to find the target element and insert after it
      if (this.targetElement && this.targetElement.parentElement) {
        this.targetElement.parentElement.insertBefore(widget, this.targetElement.nextSibling);
      } else if (this.originalAppElement && this.originalAppElement.parentElement) {
        this.originalAppElement.parentElement.insertBefore(widget, this.originalAppElement.nextSibling);
      } else {
        return;
      }
    }

    // Guardar instância no elemento para o listener global poder chamar selectOption
    widget._subscriptionWidgetInstance = this;

    // Setup event listeners
    this.setupEventListeners(widget);

    // DON'T set quantity on initialization - subscription is not selected by default
    // The form should stay clean for one-time purchase / bundles app
    // Just update the price display
    setTimeout(() => {
      this.updatePriceDisplay(widget);
    }, 100);

    // Load related products data if needed (only for products not in JSON)
    if (this.relatedProducts && this.relatedProducts.length > 0) {
      // Check if we need to load any products
      const needsLoading = this.relatedProducts.some(product => {
        const productId = product.id || product;
        return !this.productQuantities[productId] && !product.metafields?.custom?.quantidade_assinatura;
      });
      
      if (needsLoading) {
        this.loadRelatedProductsData().then(() => {
          // Update quantity section if it exists
          const widget = document.querySelector('.subscription-widget');
          if (widget) {
            const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
            if (recurringOption) {
              const quantitySection = recurringOption.querySelector('.subscription-widget__quantity-section');
              const availableQuantities = this.getAvailableQuantities();
              
              if (availableQuantities.length > 0) {
                const quantityHTML = this.getQuantitySectionHTML();
                
                if (quantitySection) {
                  quantitySection.outerHTML = quantityHTML;
                } else {
                  const intervalSection = recurringOption.querySelector('.subscription-widget__interval-section');
                  if (intervalSection) {
                    intervalSection.insertAdjacentHTML('afterend', quantityHTML);
                  }
                }
                
                this.setupQuantityListeners(widget);
              }
            }
          }
        });
      }
    }
  }

  findAddToCartButton() {
    // Try multiple selectors
    const selectors = [
      '.product-form__submit',
      'button[type="submit"][name="add"]',
      '.product-form button[type="submit"]'
    ];

    for (const selector of selectors) {
      const button = document.querySelector(selector);
      if (button) {
        return button;
      }
    }

    return null;
  }

  getWidgetHTML() {
    // Always show quantity section with fixed options (2, 3, 4)
    const quantitySection = this.getQuantitySectionHTML();
    
    // Get one-time purchase price
    const oneTimePrice = this.getOneTimePrice();

    return `
      <div class="subscription-widget__header">
        Deseja assinar e economizar?
      </div>
      <div class="subscription-widget__options">
        ${this.getOneTimeOptionHTML(oneTimePrice)}
        ${this.getRecurringOptionHTML(quantitySection)}
      </div>
    `;
  }

  getOneTimeOptionHTML(price = '') {
    const isSelected = this.selectedOption === 'one-time';
    return `
      <div class="subscription-widget__option ${isSelected ? 'subscription-widget__option--selected' : ''}" data-option="one-time">
        <div class="subscription-widget__radio-wrapper">
          <div class="subscription-widget__radio ${isSelected ? 'subscription-widget__radio--selected' : ''}"></div>
          <span class="subscription-widget__option-label">Comprar uma vez</span>
        </div>
        <div class="subscription-widget__price-wrapper">
          <span class="subscription-widget__price-current">${price}</span>
        </div>
      </div>
    `;
  }

  getOneTimePrice() {
    // Get the regular product price in cents (one-time purchase price)
    let basePriceInCents = null;
    
    // First, try to get from window.pmpr_product_variants (Pumper Bundles app data - most reliable)
    if (window.pmpr_product_variants && window.pmpr_product_variants.length > 0) {
      const variant = window.pmpr_product_variants[0];
      // Use price (not compare_at_price for one-time purchase)
      if (variant.price && variant.price > 0) {
        basePriceInCents = parseInt(variant.price);
      }
    }
    
    // Second, try product JSON element
    if (basePriceInCents === null) {
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          if (productData.selected_or_first_available_variant) {
            const variant = productData.selected_or_first_available_variant;
            if (variant.price && variant.price > 0) {
              basePriceInCents = parseInt(variant.price);
            }
          }
        } catch (e) {
        }
      }
    }
    
    // Third, try to extract from DOM
    if (basePriceInCents === null) {
      const priceContainer = document.querySelector('[id^="price-template"] .price.price--large');
      if (priceContainer) {
        const priceElement = priceContainer.querySelector('.price__regular .price-item.price-item--regular');
        if (priceElement) {
          const priceText = priceElement.textContent.trim();
          const priceMatch = priceText.match(/(\d+),(\d{2})/);
          if (priceMatch) {
            const priceValue = parseFloat(priceMatch[1] + '.' + priceMatch[2]);
            if (!isNaN(priceValue) && priceValue > 0) {
              basePriceInCents = Math.round(priceValue * 100);
            }
          }
        }
      }
    }
    
    if (basePriceInCents === null || basePriceInCents === 0) {
      return 'Carregando...';
    }
    
    // Format price in Brazilian format
    return this.formatPrice(basePriceInCents);
  }

  getRecurringOptionHTML(quantitySection = '') {
    // Get prices with quantity 2x (default)
    const quantity = this.selectedQuantity || 2;
    const priceOld = this.getPrice('old', false, quantity);
    const priceCurrent = this.getPrice('current', true, quantity);
    const cashbackPercent = this.getCashbackPercentage();

    // Check if recurring is selected
    const isSelected = this.selectedOption === 'recurring';
    return `
      <div class="subscription-widget__option subscription-widget__option--recurring ${isSelected ? 'subscription-widget__option--selected' : ''}" data-option="recurring">
        <div class="subscription-widget__badge">Mais economia</div>
        <div class="subscription-widget__radio-wrapper">
          <div class="subscription-widget__radio ${isSelected ? 'subscription-widget__radio--selected' : ''}"></div>
          <span class="subscription-widget__option-label">Plano de Assinatura Wigow</span>
        </div>
        <div class="subscription-widget__price-wrapper">
          ${priceOld ? `<span class="subscription-widget__price-old">${priceOld}</span>` : ''}
          <span class="subscription-widget__price-current">${priceCurrent}</span>
        </div>
        <div class="subscription-widget__expandable-content">
          <ul class="subscription-widget__benefits">
            <li class="subscription-widget__benefit">
              <svg class="subscription-widget__benefit-icon" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
              </svg>
              <span>Economia e tranquilidade na entrega</span>
            </li>
            <li class="subscription-widget__benefit">
              <svg class="subscription-widget__benefit-icon" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
              </svg>
              <span>Pause, reagende ou altere quando quiser</span>
            </li>
          </ul>
          <div class="subscription-widget__interval-section">
            <div class="subscription-widget__interval-title">Receba a cada</div>
            <div class="subscription-widget__interval-options">
              <div class="subscription-widget__interval-option subscription-widget__interval-option--selected" data-interval="30-day">
                <span class="subscription-widget__interval-option-label">30 dias</span>
                <span class="subscription-widget__interval-option-save">Economize</span>
              </div>
              <div class="subscription-widget__interval-option" data-interval="45-day">
                <span class="subscription-widget__interval-option-label">45 dias</span>
                <span class="subscription-widget__interval-option-save">Economize</span>
              </div>
              <div class="subscription-widget__interval-option" data-interval="60-day">
                <span class="subscription-widget__interval-option-label">60 dias</span>
                <span class="subscription-widget__interval-option-save">Economize</span>
              </div>
            </div>
          </div>
          ${quantitySection}
        </div>
      </div>
    `;
  }

  getQuantitySectionHTML() {
    // Always show quantity section with fixed options (2, 3, 4)
    // Get available quantities
    const quantities = this.getAvailableQuantities();
    if (quantities.length === 0) {
      return '';
    }

    // Get current product variant ID (always use current product)
    let currentVariantId = this.currentVariantId;
    if (!currentVariantId) {
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          if (productData.variants && productData.variants.length > 0) {
            const variant = productData.variants.find(v => v.available) || productData.variants[0];
            currentVariantId = variant.id;
          }
        } catch (e) {
        }
      }
    }

    const quantityOptions = quantities.map(qty => {
      const isSelected = qty === this.selectedQuantity;
      
      return `
        <div class="subscription-widget__quantity-option ${isSelected ? 'subscription-widget__quantity-option--selected' : ''}" 
             data-quantity="${qty}">
          ${qty}
        </div>
      `;
    }).join('');

    return `
      <div class="subscription-widget__quantity-section">
        <div class="subscription-widget__quantity-title">Quantidade de Produtos</div>
        <div class="subscription-widget__quantity-options">
          ${quantityOptions}
        </div>
      </div>
    `;
  }

  getAvailableQuantities() {
    // Fixed quantities: 2, 3, 4 (removed 1 and 5)
    const fixedQuantities = [2, 3, 4];
    
    // Filter to only include valid quantities (2, 3, 4)
    const quantities = fixedQuantities.filter(qty => qty !== 1 && qty !== 5);

    return quantities.sort((a, b) => a - b);
  }

  async loadRelatedProductsData() {
    // Check if products already have metafields from JSON
    const needsLoading = [];
    
    for (const product of this.relatedProducts) {
      const productId = product.id || product;
      
      // If product already has metafields from JSON, skip loading
      if (product.metafields?.custom?.quantidade_assinatura) {
        // Already processed in getCurrentProductData
        continue;
      }
      
      // Only load if we don't have the quantity yet
      if (!this.productQuantities[productId] && (product.id || product.handle)) {
        needsLoading.push(product);
      }
    }

    // Load product data only for products that need it
    const loadPromises = [];
    
    for (const product of needsLoading) {
      const promise = this.loadProductData(product).then(productData => {
        if (productData && productData.variants && productData.variants.length > 0) {
          // Only store product data if it has variants
          if (!this.relatedProductsData) {
            this.relatedProductsData = {};
          }
          this.relatedProductsData[productData.id] = productData;
          
          // Extract quantity from metafield if available
          if (productData.metafields?.custom?.quantidade_assinatura) {
            const qty = parseInt(productData.metafields.custom.quantidade_assinatura);
            if (qty && !isNaN(qty)) {
              this.productQuantities[productData.id] = qty;
            }
          }
        } else {
        }
      }).catch(e => {
      });
      loadPromises.push(promise);
    }

    await Promise.all(loadPromises);
    
    // Update quantity section if widget already exists (now with full product data including variants)
    const widget = document.querySelector('.subscription-widget');
    if (widget) {
      const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
      if (recurringOption) {
        const quantitySection = recurringOption.querySelector('.subscription-widget__quantity-section');
        const availableQuantities = this.getAvailableQuantities();
        
        if (availableQuantities.length > 0) {
          // Regenerate HTML with updated product/variant data
          const quantityHTML = this.getQuantitySectionHTML();
          
          if (quantitySection) {
            // Update existing quantity section
            quantitySection.outerHTML = quantityHTML;
          } else {
            // Add quantity section after interval section
            const intervalSection = recurringOption.querySelector('.subscription-widget__interval-section');
            if (intervalSection) {
              intervalSection.insertAdjacentHTML('afterend', quantityHTML);
            }
          }
          
          // Setup listeners
          this.setupQuantityListeners(widget);
          
        } else if (quantitySection) {
          // Remove quantity section if no quantities available
          quantitySection.remove();
        }
      }
    }
  }

  async loadProductData(product) {
    try {
      // Try to get handle from product object or construct from ID
      let handle = product.handle;
      
      if (!handle && product.id) {
        // If we have a product reference, try to get handle from URL or fetch
        const productLink = document.querySelector(`a[href*="/products/"][data-product-id="${product.id}"]`);
        if (productLink) {
          const href = productLink.getAttribute('href');
          const match = href.match(/\/products\/([^\/\?]+)/);
          if (match) {
            handle = match[1];
          }
        }
      }

      if (!handle) {
        return null;
      }

      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        const productData = await response.json();
        
        // Check if product has variants
        if (!productData.variants || productData.variants.length === 0) {
          return null;
        }
        return productData;
      } else {
      }
    } catch (e) {
    }
    return null;
  }

  getPrice(type, isSubscription = false, quantity = null) {
    // Get the base price (regular product price) - always unit price
    let basePriceInCents = null;
    
    // Use selected quantity if not provided
    if (quantity === null) {
      quantity = this.selectedQuantity || 2;
    }
    
    // Ensure quantity is a valid number and within allowed range (2, 3, 4)
    quantity = parseInt(quantity) || 2;
    if (quantity < 2) quantity = 2;
    if (quantity > 4) quantity = 4;
    
    // Helper function to extract price from element text
    const extractPriceFromElement = (element) => {
      if (!element) return null;
      
      // Get text content and clean it up
      let priceText = element.textContent.trim();
      // Remove any whitespace, line breaks, and currency symbols
      priceText = priceText.replace(/\s+/g, '').replace(/R\$/g, '');
      
      // Match pattern: digits with comma as decimal separator (Brazilian format)
      // e.g., "59,90" -> extracts "59" and "90"
      const priceMatch = priceText.match(/(\d+),(\d{2})/);
      if (priceMatch) {
        const priceValue = parseFloat(priceMatch[1] + '.' + priceMatch[2]);
        if (!isNaN(priceValue) && priceValue > 0) {
          return Math.round(priceValue * 100);
        }
      }
      
      // Also try with dot as decimal separator
      const priceDotMatch = priceText.match(/(\d+)\.(\d{2})/);
      if (priceDotMatch) {
        const priceValue = parseFloat(priceDotMatch[1] + '.' + priceDotMatch[2]);
        if (!isNaN(priceValue) && priceValue > 0) {
          return Math.round(priceValue * 100);
        }
      }
      
      return null;
    };
    
    // Check if there's a REAL sale (price--on-sale class must be present)
    // Only use compare price when there's an actual discount
    // Target only the main product price container, not bundles app prices
    const mainPriceContainer = document.querySelector('[id^="price-template"] .price--on-sale');
    
    if (mainPriceContainer) {
      // There's a real sale - use the compare price (original price before discount)
      const comparePriceElement = mainPriceContainer.querySelector('.price__compare s.price-item.price-item--regular');
      if (comparePriceElement) {
        const price = extractPriceFromElement(comparePriceElement);
        if (price) {
          basePriceInCents = price;
        }
      }
    }
    
    // If no compare price found, use regular price selectors
    if (basePriceInCents === null) {
      // First, try to get from window.pmpr_product_variants (Pumper Bundles app data - most reliable)
      if (window.pmpr_product_variants && window.pmpr_product_variants.length > 0) {
        const variant = window.pmpr_product_variants[0];
        // Use compare_at_price if available and greater than price, otherwise use price
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          basePriceInCents = parseInt(variant.compare_at_price);
        } else if (variant.price && variant.price > 0) {
          basePriceInCents = parseInt(variant.price);
        }
      }
      
      // Second, try product JSON element
      if (basePriceInCents === null) {
        const productJson = document.querySelector('[data-product-json]');
        if (productJson) {
          try {
            const productData = JSON.parse(productJson.textContent);
            if (productData.selected_or_first_available_variant) {
              const variant = productData.selected_or_first_available_variant;
              if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                basePriceInCents = parseInt(variant.compare_at_price);
              } else if (variant.price && variant.price > 0) {
                basePriceInCents = parseInt(variant.price);
              }
            }
          } catch (e) {
          }
        }
      }
      
      // If JSON sources didn't work, try DOM selectors with very specific paths
      if (basePriceInCents === null) {
        // Target ONLY the main product price container by ID pattern
        const priceContainer = document.querySelector('[id^="price-template"] .price.price--large');
        if (priceContainer) {
          const priceElement = priceContainer.querySelector('.price__regular .price-item.price-item--regular');
          if (priceElement) {
            basePriceInCents = extractPriceFromElement(priceElement);
          }
        }
        
        // Fallback to other selectors if still not found
        if (basePriceInCents === null) {
          const fallbackSelectors = [
            '#ProductInfo-template--20583548092662__main .price--large .price__regular .price-item--regular',
            '.product__info-container .price--large .price__regular .price-item--regular'
          ];
          
          for (const selector of fallbackSelectors) {
            const priceElement = document.querySelector(selector);
            const price = extractPriceFromElement(priceElement);
            if (price) {
              basePriceInCents = price;
              break;
            }
          }
        }
      }
    }

    if (basePriceInCents === null || basePriceInCents === 0) {
      return 'R$ 0,00';
    }

    // Multiply base unit price by quantity to get total price
    const totalPriceInCents = basePriceInCents * quantity;

    // For 'old' price (original price without discount), return total
    if (type === 'old') {
      return this.formatPrice(totalPriceInCents);
    }

    // If it's a subscription and we need the current (discounted) price
    if (isSubscription && type === 'current') {
      // Get discount percentage from app widget
      const discountPercent = this.getDiscountPercentage();
      if (discountPercent > 0) {
        // Calculate discounted price (apply discount to total original price)
        const discountAmount = Math.round((totalPriceInCents * discountPercent) / 100);
        const discountedPrice = totalPriceInCents - discountAmount;
        return this.formatPrice(discountedPrice);
      }
    }

    // Return regular price (multiplied by quantity)
    return this.formatPrice(totalPriceInCents);
  }

  getDiscountPercentage() {
    if (!this.originalAppElement) {
      return 0;
    }

    // Try to get discount from app widget hidden inputs
    const discountValue = this.originalAppElement.querySelector('.sls-input-discount-value');
    if (discountValue && discountValue.value) {
      const discountVal = String(discountValue.value).trim();
      const discountMatch = discountVal.match(/(\d+)%/);
      if (discountMatch) {
        return parseInt(discountMatch[1]);
      }
      // Try without %
      const numMatch = discountVal.match(/(\d+)/);
      if (numMatch) {
        return parseInt(numMatch[1]);
      }
    }

    // Also try from all hidden inputs
    const appHiddenInputs = this.originalAppElement.querySelectorAll('input.sls-cart-attribute.sls-hidden');
    for (const input of appHiddenInputs) {
      if (input.classList.contains('sls-input-discount-value') && input.value) {
        const discountVal = String(input.value).trim();
        const discountMatch = discountVal.match(/(\d+)%/);
        if (discountMatch) {
          return parseInt(discountMatch[1]);
        }
        const numMatch = discountVal.match(/(\d+)/);
        if (numMatch) {
          return parseInt(numMatch[1]);
        }
      }
    }

    // Try from discount description
    const discountDesc = this.originalAppElement.querySelector('.sls-discount-description-container');
    if (discountDesc) {
      const descText = discountDesc.textContent || '';
      const descMatch = descText.match(/(\d+)%/);
      if (descMatch) {
        return parseInt(descMatch[1]);
      }
    }

    return 0;
  }

  getCashbackPercentage() {
    if (!this.originalAppElement) {
      return 0;
    }

    // Try to get cashback from app widget - look for specific cashback indicators
    const cashbackInputs = this.originalAppElement.querySelectorAll('.sls-input-cashback, .sls-cashback-value, input[name*="cashback"], input[class*="cashback"]');
    for (const input of cashbackInputs) {
      if (input.value) {
        const cashbackVal = String(input.value).trim();
        const cashbackMatch = cashbackVal.match(/(\d+)%/);
        if (cashbackMatch) {
          return parseInt(cashbackMatch[1]);
        }
        const numMatch = cashbackVal.match(/(\d+)/);
        if (numMatch) {
          return parseInt(numMatch[1]);
        }
      }
    }

    // Try to find cashback in all hidden inputs
    const allHiddenInputs = this.originalAppElement.querySelectorAll('input[type="hidden"]');
    for (const input of allHiddenInputs) {
      const name = input.name || '';
      const value = input.value || '';
      if ((name.toLowerCase().includes('cashback') || input.className.toLowerCase().includes('cashback')) && value) {
        const cashbackVal = String(value).trim();
        const cashbackMatch = cashbackVal.match(/(\d+)%/);
        if (cashbackMatch) {
          return parseInt(cashbackMatch[1]);
        }
        const numMatch = cashbackVal.match(/(\d+)/);
        if (numMatch) {
          return parseInt(numMatch[1]);
        }
      }
    }

    // If no specific cashback field, use discount percentage as cashback
    // (many subscription apps use discount as cashback)
    const discountPercent = this.getDiscountPercentage();
    if (discountPercent > 0) {
      return discountPercent;
    }

    // Try to find cashback in text descriptions
    const cashbackText = this.originalAppElement.textContent || '';
    const cashbackMatch = cashbackText.match(/(\d+)%\s*(?:de\s*)?cashback/i);
    if (cashbackMatch) {
      return parseInt(cashbackMatch[1]);
    }

    return 0;
  }

  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }

  updatePriceDisplay(widget) {
    if (!widget) return;

    // Update one-time option price
    const oneTimeOption = widget.querySelector('.subscription-widget__option[data-option="one-time"]');
    if (oneTimeOption) {
      const priceWrapper = oneTimeOption.querySelector('.subscription-widget__price-wrapper');
      if (priceWrapper) {
        const oneTimePrice = this.getOneTimePrice();
        const currentPriceSpan = priceWrapper.querySelector('.subscription-widget__price-current');
        if (currentPriceSpan) {
          currentPriceSpan.textContent = oneTimePrice;
        }
      }
    }

    // Use only the subscription widget's selected quantity (2, 3, or 4)
    // Don't read from form as it might be set by other apps (bundles, etc.)
    let quantity = this.selectedQuantity || 2;
    
    // Ensure quantity is one of the valid options (2, 3, 4)
    if (quantity < 2) quantity = 2;
    if (quantity > 4) quantity = 4;
    quantity = parseInt(quantity) || 2;

    // Update recurring option price
    const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
    if (recurringOption) {
      const priceWrapper = recurringOption.querySelector('.subscription-widget__price-wrapper');
      if (priceWrapper) {
        // Get prices with quantity - explicitly pass quantity to ensure it's used
        const priceOld = this.getPrice('old', false, quantity);
        const priceCurrent = this.getPrice('current', true, quantity);
        
        const oldPriceSpan = priceWrapper.querySelector('.subscription-widget__price-old');
        const currentPriceSpan = priceWrapper.querySelector('.subscription-widget__price-current');
        
        if (oldPriceSpan) {
          oldPriceSpan.textContent = priceOld;
        }
        if (currentPriceSpan) {
          currentPriceSpan.textContent = priceCurrent;
        }
      }

      // Update cashback percentage
      const cashbackPercent = this.getCashbackPercentage();
      const cashbackSpan = recurringOption.querySelector('.subscription-widget__benefit span');
      if (cashbackSpan && cashbackSpan.textContent.includes('cashback')) {
        cashbackSpan.textContent = cashbackPercent > 0 ? `${cashbackPercent}% de cashback` : 'Cashback disponível';
      }
    }
  }

  setupEventListeners(widget) {
    // One-time option selection
    const oneTimeOption = widget.querySelector('.subscription-widget__option[data-option="one-time"]');
    if (oneTimeOption) {
      oneTimeOption.addEventListener('click', (e) => {
        this.selectOption('one-time', widget);
      });
    }

    // Option selection - toggle subscription on/off
    const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
    if (recurringOption) {
      recurringOption.addEventListener('click', (e) => {
        // Prevent triggering when clicking on interval or quantity options
        if (e.target.closest('.subscription-widget__interval-option') || 
            e.target.closest('.subscription-widget__quantity-option')) {
          return;
        }
        this.selectOption('recurring', widget);
      });
    }

    // Interval selection
    const intervalOptions = widget.querySelectorAll('.subscription-widget__interval-option');
    intervalOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent toggle
        const interval = option.dataset.interval;
        this.selectInterval(interval, widget);
      });
    });

    // Quantity selection
    this.setupQuantityListeners(widget);

    // Initialize with one-time selected by default
    if (this.selectedOption === 'one-time') {
      this.selectOption('one-time', widget);
    } else {
      // Update quantity options UI for subscription
      const quantityOptions = widget.querySelectorAll('.subscription-widget__quantity-option');
      quantityOptions.forEach(opt => {
        opt.classList.remove('subscription-widget__quantity-option--selected');
        if (parseInt(opt.dataset.quantity) === this.selectedQuantity) {
          opt.classList.add('subscription-widget__quantity-option--selected');
        }
      });

      // Set SUBSCRIPTION in app original
      if (this.originalAppElement) {
        const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
        if (subscriptionRadio && !subscriptionRadio.checked) {
          subscriptionRadio.checked = true;
          setTimeout(() => {
            subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        }
      }

      // Disable bundles app when subscription is selected
      this.toggleQuantityBreaksApp(false);

      // Update form with subscription quantity
      this.selectQuantity(this.selectedQuantity || 2, widget);
    }

    // Setup listener to prevent bundles app changes when subscription is active
    this.setupBundlesAppInterceptor();

    // Copy properties from app original to form before submit
    this.setupFormSubmitHandler();
  }

  setupBundlesAppInterceptor() {
    const bundlesWrapper = document.querySelector('.block__cbwrapper');
    if (!bundlesWrapper) return;

    // Intercept clicks on the bundles app
    bundlesWrapper.addEventListener('click', (e) => {
      // Check if subscription is active
      const widget = document.querySelector('.subscription-widget');
      const isSubscriptionActive = widget && 
        widget.querySelector('.subscription-widget__option--recurring.subscription-widget__option--selected');

      if (isSubscriptionActive) {
        // Prevent the click from changing the selection
        e.preventDefault();
        e.stopPropagation();
        
        // Force single item selection
        const singleItemRadio = bundlesWrapper.querySelector('input[type="radio"][value="3"]') || 
                                bundlesWrapper.querySelector('input.prvw__quantity_input3');
        if (singleItemRadio) {
          singleItemRadio.disabled = false;
          singleItemRadio.checked = true;
          singleItemRadio.disabled = true;
        }
        
        return false;
      }
    }, true); // Use capture phase

    // Also intercept change events on the radio buttons
    const allRadios = bundlesWrapper.querySelectorAll('input[type="radio"]');
    allRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const widget = document.querySelector('.subscription-widget');
        const isSubscriptionActive = widget && 
          widget.querySelector('.subscription-widget__option--recurring.subscription-widget__option--selected');

        if (isSubscriptionActive && radio.value !== '3') {
          // Revert to single item
          e.preventDefault();
          e.stopPropagation();
          
          const singleItemRadio = bundlesWrapper.querySelector('input[type="radio"][value="3"]');
          if (singleItemRadio) {
            singleItemRadio.disabled = false;
            singleItemRadio.checked = true;
            singleItemRadio.disabled = true;
          }
          radio.checked = false;
        }
      }, true);
    });
  }

  setupFormSubmitHandler() {
    // Intercept FormData to add subscription properties if app doesn't add them
    const originalFormData = window.FormData;
    const self = this;
    
    window.FormData = function(form) {
      const formData = new originalFormData(form);
      
      // Check if this is our product form and subscription is active
      if (form && form.getAttribute('data-type') === 'add-to-cart-form') {
        const widget = document.querySelector('.subscription-widget');
        const isSubscriptionActive = widget && 
          widget.querySelector('.subscription-widget__option--recurring.subscription-widget__option--selected');
        
        if (isSubscriptionActive) {
          // Check if subscription properties are already in FormData
          let hasSubscriptionProps = false;
          for (const [key, value] of formData.entries()) {
            if (key.startsWith('properties[')) {
              const match = key.match(/properties\[(.+)\]/);
              if (match) {
                const propKey = match[1];
                if (propKey.startsWith('_sealsub_') || 
                    propKey === 'Intervalo de assinatura' || 
                    propKey === 'Desconto no próximo pedido' ||
                    propKey.includes('Intervalo') ||
                    propKey.includes('Desconto') ||
                    propKey.includes('sealsub') ||
                    propKey.toLowerCase().includes('subscription') ||
                    propKey.toLowerCase().includes('assinatura')) {
                  hasSubscriptionProps = true;
                  break;
                }
              }
            }
          }
          
          // If no subscription properties found, create them manually
          if (!hasSubscriptionProps) {
            
            // First, try to get properties from form inputs
            const formInputs = form.querySelectorAll('input[type="hidden"][name^="properties["]');
            let foundProps = false;
            formInputs.forEach(input => {
              const name = input.name;
              if (!name || !name.startsWith('properties[')) return;
              
              const match = name.match(/properties\[(.+)\]/);
              if (!match) return;
              
              const key = match[1];
              const isSubscriptionProperty = key.startsWith('_sealsub_') || 
                                             key === 'Intervalo de assinatura' || 
                                             key === 'Desconto no próximo pedido' ||
                                             key.includes('Intervalo') ||
                                             key.includes('Desconto') ||
                                             key.includes('sealsub') ||
                                             key.toLowerCase().includes('subscription') ||
                                             key.toLowerCase().includes('assinatura');
              
              if (isSubscriptionProperty && input.value) {
                formData.append(name, input.value);
                foundProps = true;
              }
            });
            
            // If still no properties, try to get them from app original
            if (!foundProps && self.originalAppElement) {
              const appInputs = self.originalAppElement.querySelectorAll('input[type="hidden"][name^="properties["]');
              appInputs.forEach(input => {
                const name = input.name;
                if (!name || !name.startsWith('properties[')) return;
                
                const match = name.match(/properties\[(.+)\]/);
                if (!match) return;
                
                const key = match[1];
                const isSubscriptionProperty = key.startsWith('_sealsub_') || 
                                               key === 'Intervalo de assinatura' || 
                                               key === 'Desconto no próximo pedido' ||
                                               key.includes('Intervalo') ||
                                               key.includes('Desconto') ||
                                               key.includes('sealsub') ||
                                               key.toLowerCase().includes('subscription') ||
                                               key.toLowerCase().includes('assinatura');
                
                if (isSubscriptionProperty && input.value) {
                  formData.append(name, input.value);
                  foundProps = true;
                }
              });
            }
            
            // If still no properties, CREATE THEM MANUALLY based on selected values
            if (!foundProps) {
              
              // First, try to create them in the form (so they're available for FormData)
              self.createSubscriptionPropertiesManually(form);
              
              // Then add them to FormData
              const intervalInput = form.querySelector('input[name="properties[Intervalo de assinatura]"]');
              const discountInput = form.querySelector('input[name="properties[Desconto no próximo pedido]"]');
              
              if (intervalInput) {
                formData.append('properties[Intervalo de assinatura]', intervalInput.value);
              }
              
              if (discountInput) {
                formData.append('properties[Desconto no próximo pedido]', discountInput.value);
              }
            }
          } else {
          }
        }
      }
      
      return formData;
    };
    
    // Listen to form submit to copy properties from app original
    const forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        this.handleFormSubmit(form);
      }, true); // Use capture phase to run before other handlers
    });

    // Also listen to button clicks as backup
    const submitButtons = document.querySelectorAll('button[type="submit"][name="add"], .product-form__submit');
    submitButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const form = button.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
        if (form) {
          // Handle form submission on click
          this.handleFormSubmit(form);
        }
      }, true); // Use capture phase
    });

    // Intercept product-form custom element's submit handler
    const productFormElements = document.querySelectorAll('product-form');
    productFormElements.forEach(productForm => {
      const originalOnSubmit = productForm.onSubmitHandler;
      if (originalOnSubmit) {
        productForm.onSubmitHandler = (evt) => {
          const form = productForm.form || productForm.querySelector('form[data-type="add-to-cart-form"]');
          if (form) {
            this.handleFormSubmit(form);
          }
          return originalOnSubmit.call(productForm, evt);
        };
      }
    });
  }

  handleFormSubmit(form) {
    if (!form) return;

    // Check which option is selected
    const widget = document.querySelector('.subscription-widget');
    const isOneTimeSelected = widget && 
      widget.querySelector('.subscription-widget__option[data-option="one-time"].subscription-widget__option--selected');
    const isSubscriptionActive = widget && 
      widget.querySelector('.subscription-widget__option--recurring.subscription-widget__option--selected');

    if (isOneTimeSelected) {
      
      // One-time purchase - remove all subscription properties
      this.removeSubscriptionProperties(form);
      
      // CRITICAL: Set quantity to 1 - check ALL possible quantity inputs
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = '1';
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      
      // Also check quantity-input custom element
      const quantityInputContainer = form.querySelector('quantity-input');
      if (quantityInputContainer) {
        const innerInput = quantityInputContainer.querySelector('input');
        if (innerInput && innerInput.value !== '1') {
          innerInput.value = '1';
          innerInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      
      // Final verification
      const finalQuantityInput = form.querySelector('input[name="quantity"]');
      if (finalQuantityInput && finalQuantityInput.value !== '1') {
        finalQuantityInput.value = '1';
        finalQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Update app widget to one-time if needed
      if (this.originalAppElement) {
        const oneTimeRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
        if (oneTimeRadio && !oneTimeRadio.checked) {
          oneTimeRadio.checked = true;
          oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      
    } else if (isSubscriptionActive) {
      // Assinatura só pode ser do produto da página (original), nunca do progressive/metafield
      const progressiveDiscount = document.querySelector('[data-progressive-discount]');
      const originalOption = progressiveDiscount && progressiveDiscount.querySelector('.progressive-discount__option[data-is-related="false"]');
      if (originalOption && originalOption.dataset.variantId) {
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value !== originalOption.dataset.variantId) {
          variantInput.removeAttribute('disabled');
          variantInput.value = originalOption.dataset.variantId;
        }
      }

      // Subscription is active - disable bundles app inputs
      this.disableBundlesAppInputs(form);
      
      // Ensure app original is in subscription mode
      if (this.originalAppElement) {
        const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
        const oneTimeRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
        
        // Uncheck one-time if it's checked
        if (oneTimeRadio && oneTimeRadio.checked) {
          oneTimeRadio.checked = false;
          oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        // Check subscription
        if (subscriptionRadio && !subscriptionRadio.checked) {
          subscriptionRadio.checked = true;
          subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
          subscriptionRadio.dispatchEvent(new Event('click', { bubbles: true }));
        }
        
        // Update interval select to match selected interval
        const intervalSelect = this.originalAppElement.querySelector('select[name="subs_interval"]');
        if (intervalSelect) {
          const valueMap = {
            '30-day': '30 day',
            '45-day': '45 day',
            '60-day': '60 day'
          };
          const targetValue = valueMap[this.selectedInterval] || '30 day';
          if (intervalSelect.value !== targetValue) {
            intervalSelect.value = targetValue;
            intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
            intervalSelect.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      }
      
      // Update quantity to match subscription selection
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = this.selectedQuantity || 2;
      }
      
      // Force copy subscription properties from app original - try multiple times
      this._doCopyProperties(form);
      
      // Wait a bit and try again
      setTimeout(() => {
        this._doCopyProperties(form);
      }, 100);
      
      // CRITICAL: If app doesn't create properties, create them manually
      setTimeout(() => {
        const existingProps = form.querySelectorAll('input[name^="properties["][name*="sealsub"], input[name^="properties["][name*="Intervalo"], input[name^="properties["][name*="Desconto"]');
        if (existingProps.length === 0) {
          this.createSubscriptionPropertiesManually(form);
        }
      }, 200);
      
      // CRITICAL: The app may add properties during form submission
      // We need to check multiple times and potentially delay submission
      let propertiesFound = false;
      let checkAttempts = 0;
      const maxAttempts = 10;
      
      const checkForProperties = () => {
        checkAttempts++;
        const existingProps = form.querySelectorAll('input[name^="properties["][name*="sealsub"], input[name^="properties["][name*="Intervalo"], input[name^="properties["][name*="Desconto"]');
        
        // Also check for properties with subscription-related keys
        const allProps = form.querySelectorAll('input[name^="properties["]');
        let subscriptionProps = [];
        allProps.forEach(prop => {
          const match = prop.name.match(/properties\[(.+)\]/);
          if (match) {
            const key = match[1];
            if (key.startsWith('_sealsub_') || 
                key === 'Intervalo de assinatura' || 
                key === 'Desconto no próximo pedido' ||
                key.includes('Intervalo') ||
                key.includes('Desconto') ||
                key.includes('sealsub') ||
                key.toLowerCase().includes('subscription') ||
                key.toLowerCase().includes('assinatura')) {
              subscriptionProps.push(prop);
            }
          }
        });
        
        
        if (subscriptionProps.length > 0) {
          propertiesFound = true;
          subscriptionProps.forEach(prop => {
            const match = prop.name.match(/properties\[(.+)\]/);
            const key = match ? match[1] : prop.name;
          });
          return true;
        }
        
        if (checkAttempts < maxAttempts) {
          // Try to force app to add properties
          if (checkAttempts === 1) {
            // First attempt: force recreate
            this.forceRecreateSubscriptionProperties();
          } else if (checkAttempts === 3) {
            // Third attempt: trigger interval change
            const intervalSelect = this.originalAppElement?.querySelector('select[name="subs_interval"]');
            if (intervalSelect) {
              const currentValue = intervalSelect.value;
              intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
              intervalSelect.dispatchEvent(new Event('input', { bubbles: true }));
            }
          } else if (checkAttempts === 5) {
            // Fifth attempt: try copying again
            this._doCopyProperties(form);
          }
          
          // Check again after a short delay
          setTimeout(checkForProperties, 50);
          return false;
        }
        
        return false;
      };
      
      // Start checking
      if (!checkForProperties()) {
      }
      
      // Also set up a final check right before form actually submits
      // We'll use a small delay to allow the app to add properties
      const originalSubmit = form.submit;
      form.submit = function(...args) {
        // Give app one last chance to add properties
        setTimeout(() => {
          const finalProps = form.querySelectorAll('input[name^="properties["][name*="sealsub"], input[name^="properties["][name*="Intervalo"], input[name^="properties["][name*="Desconto"]');
          if (finalProps.length === 0) {
          }
          originalSubmit.apply(form, args);
        }, 100);
      };
    }
  }

  removeSubscriptionProperties(form) {
    if (!form) return;

    // Remove all subscription-related property inputs
    const subscriptionInputs = form.querySelectorAll('input[name^="properties["]');
    subscriptionInputs.forEach(input => {
      const name = input.name || '';
      const match = name.match(/properties\[(.+)\]/);
      if (match) {
        const key = match[1];
        // Remove subscription-related properties
        if (key.startsWith('_sealsub_') || 
            key === 'Intervalo de assinatura' || 
            key === 'Desconto no próximo pedido' ||
            key.includes('Intervalo') ||
            key.includes('Desconto') ||
            key.includes('sealsub') ||
            key.toLowerCase().includes('subscription') ||
            key.toLowerCase().includes('assinatura')) {
          input.remove();
        }
      }
    });
  }

  disableBundlesAppInputs(form) {
    // Find and disable/remove any inputs from the Pumper Bundles app
    // These inputs usually have names like "properties[_pmpr_...]" or similar
    const bundlesInputs = form.querySelectorAll('input[name^="properties[_pmpr"]');
    bundlesInputs.forEach(input => {
      input.disabled = true;
      input.remove();
    });

    // Also check for hidden inputs that might be added by the bundles app
    const allHiddenInputs = form.querySelectorAll('input[type="hidden"]');
    allHiddenInputs.forEach(input => {
      const name = input.name || '';
      // Remove any bundles-related properties (but not subscription properties)
      if (name.includes('_pmpr') || name.includes('_pumper') || name.includes('bundle')) {
        input.disabled = true;
        input.remove();
      }
    });

    // Ensure single item option is selected in the bundles app to prevent discount
    const bundlesWrapper = document.querySelector('.block__cbwrapper');
    if (bundlesWrapper) {
      // Select the single item option (value="3" or the last one)
      const singleItemRadio = bundlesWrapper.querySelector('input[type="radio"][value="3"]') || 
                              bundlesWrapper.querySelector('input.prvw__quantity_input3');
      
      if (singleItemRadio && !singleItemRadio.checked) {
        // Temporarily enable to change
        singleItemRadio.disabled = false;
        singleItemRadio.checked = true;
        singleItemRadio.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Click the label to trigger the app's handlers
        const label = bundlesWrapper.querySelector('label[for="' + singleItemRadio.id + '"]');
        if (label) {
          label.click();
        }
        
        // Disable again
        singleItemRadio.disabled = true;
      }
    }
  }

  cleanupFormForOneTime() {
    // Remove all subscription-related properties and set quantity to 1 for one-time purchase
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;


    // Remove subscription-related property inputs
    this.removeSubscriptionProperties(form);

    // Set quantity to 1 for one-time purchase - check ALL possible quantity inputs
    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.value = '1';
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Also update quantity-input custom element if it exists
    const quantityInputContainer = form.querySelector('quantity-input');
    if (quantityInputContainer) {
      const innerInput = quantityInputContainer.querySelector('input');
      if (innerInput) {
        innerInput.value = '1';
        innerInput.dispatchEvent(new Event('change', { bubbles: true }));
        innerInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Also check for any other quantity-related inputs
    const allQuantityInputs = form.querySelectorAll('input[type="number"][name*="quantity"], input[type="text"][name*="quantity"]');
    allQuantityInputs.forEach(input => {
      if (input.name === 'quantity' || input.name.includes('quantity')) {
        input.value = '1';
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    // Verify quantity is actually 1
    const finalQuantityInput = form.querySelector('input[name="quantity"]');
    if (finalQuantityInput && finalQuantityInput.value !== '1') {
      finalQuantityInput.value = '1';
      finalQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

  }

  copyPropertiesToForm(form) {
    if (!form) {
      return;
    }

    // Copy from app original to form (this will ensure app is in subscription mode first)
    this._doCopyProperties(form);
  }

  _doCopyProperties(form) {
    if (!form || !this.originalAppElement) {
      return;
    }

    // First, ensure app is in subscription mode
    const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
    const oneTimeRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
    
    // If one-time is checked, switch to subscription first
    if (oneTimeRadio && oneTimeRadio.checked) {
      oneTimeRadio.checked = false;
      oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    if (subscriptionRadio && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
      subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
      subscriptionRadio.dispatchEvent(new Event('click', { bubbles: true }));
      
      // Wait a bit for app to process before copying
      setTimeout(() => {
        this._doCopyPropertiesInternal(form);
      }, 100);
      return;
    }

    // App is in subscription mode, copy properties
    this._doCopyPropertiesInternal(form);
  }

  _doCopyPropertiesInternal(form) {
    if (!form || !this.originalAppElement) {
      return;
    }

    // First verify app is in subscription mode
    const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
    if (!subscriptionRadio || !subscriptionRadio.checked) {
      return;
    }

    let copiedCount = 0;
    let skippedCount = 0;
    
    // IMPORTANT: The app may add properties directly to the main form, not inside originalAppElement
    // So we need to check the main form for properties that match subscription patterns
    // First, let's check if properties already exist in the form (added by the app)
    const existingFormProps = form.querySelectorAll('input[type="hidden"][name^="properties["]');
    
    existingFormProps.forEach(input => {
      const name = input.name;
      if (!name || !name.startsWith('properties[')) return;
      
      const match = name.match(/properties\[(.+)\]/);
      if (!match) return;
      
      const key = match[1];
      const isSubscriptionProperty = key.startsWith('_sealsub_') || 
                                     key === 'Intervalo de assinatura' || 
                                     key === 'Desconto no próximo pedido' ||
                                     key.includes('Intervalo') ||
                                     key.includes('Desconto') ||
                                     key.includes('sealsub') ||
                                     key.toLowerCase().includes('subscription') ||
                                     key.toLowerCase().includes('assinatura');
      
      if (isSubscriptionProperty && input.value) {
        copiedCount++; // Already in form, count it
      }
    });
    
    // Also check the app original element for properties
    const appInputs = this.originalAppElement.querySelectorAll('input[type="hidden"]');
    
    appInputs.forEach(appInput => {
      const name = appInput.name;
      const value = appInput.value;
      
      // Only copy subscription-related properties
      if (!name || !name.startsWith('properties[')) {
        skippedCount++;
        return;
      }
      
      const match = name.match(/properties\[(.+)\]/);
      if (!match) {
        skippedCount++;
        return;
      }
      
      const key = match[1];
      const isSubscriptionProperty = key.startsWith('_sealsub_') || 
                                     key === 'Intervalo de assinatura' || 
                                     key === 'Desconto no próximo pedido' ||
                                     key.includes('Intervalo') ||
                                     key.includes('Desconto') ||
                                     key.includes('sealsub') ||
                                     key.toLowerCase().includes('subscription') ||
                                     key.toLowerCase().includes('assinatura');
      
      if (!isSubscriptionProperty) {
        skippedCount++;
        return;
      }
      
      if (!value || value.trim() === '') {
        skippedCount++;
        return;
      }
      
      // Check if this property already exists in the form
      let existingInput = form.querySelector(`input[name="${name}"]`);
      
      if (existingInput) {
        // Update existing input
        if (existingInput.value !== value) {
          existingInput.value = value;
        }
        copiedCount++;
      } else {
        // Create new input in the form
        const newInput = document.createElement('input');
        newInput.type = 'hidden';
        newInput.name = name;
        newInput.value = value;
        form.appendChild(newInput);
        copiedCount++;
      }
    });
    
    // Also check the entire document for any sealsubs-related inputs that might have been added
    // Some apps add inputs outside the form initially
    const allSealsubInputs = document.querySelectorAll('input[name^="properties["][name*="sealsub"], input[name^="properties["][name*="Intervalo"], input[name^="properties["][name*="Desconto"]');
    allSealsubInputs.forEach(input => {
      const name = input.name;
      if (!name || !name.startsWith('properties[')) return;
      
      const match = name.match(/properties\[(.+)\]/);
      if (!match) return;
      
      const key = match[1];
      const isSubscriptionProperty = key.startsWith('_sealsub_') || 
                                     key === 'Intervalo de assinatura' || 
                                     key === 'Desconto no próximo pedido' ||
                                     key.includes('Intervalo') ||
                                     key.includes('Desconto') ||
                                     key.includes('sealsub') ||
                                     key.toLowerCase().includes('subscription') ||
                                     key.toLowerCase().includes('assinatura');
      
      if (!isSubscriptionProperty || !input.value) return;
      
      // Check if already in our form
      const existingInput = form.querySelector(`input[name="${name}"]`);
      if (!existingInput) {
        const newInput = document.createElement('input');
        newInput.type = 'hidden';
        newInput.name = name;
        newInput.value = input.value;
        form.appendChild(newInput);
        copiedCount++;
      }
    });
    
    
    // If we still have no properties, create them manually
    if (copiedCount === 0) {
      this.createSubscriptionPropertiesManually(form);
    }
  }

  createSubscriptionPropertiesManually(form) {
    if (!form) return;
    
    
    // Check if properties already exist
    const existingInterval = form.querySelector('input[name="properties[Intervalo de assinatura]"]');
    const existingDiscount = form.querySelector('input[name="properties[Desconto no próximo pedido]"]');
    
    // Get interval value
    const intervalMap = {
      '30-day': '30 dias',
      '45-day': '45 dias',
      '60-day': '60 dias'
    };
    const intervalValue = intervalMap[this.selectedInterval] || '30 dias';
    
    // Create or update "Intervalo de assinatura" property
    if (existingInterval) {
      existingInterval.value = intervalValue;
    } else {
      const intervalInput = document.createElement('input');
      intervalInput.type = 'hidden';
      intervalInput.name = 'properties[Intervalo de assinatura]';
      intervalInput.value = intervalValue;
      form.appendChild(intervalInput);
    }
    
    // Try to get discount value
    let discountValue = '';
    if (this.originalAppElement) {
      // Look for discount description or value in the app
      const discountDesc = this.originalAppElement.querySelector('.sls-discount-description');
      if (discountDesc) {
        const text = discountDesc.textContent || '';
        const match = text.match(/(\d+)%/);
        if (match) {
          discountValue = `${match[1]}%`;
        }
      }
    }
    
    // If no discount found, try to calculate from price difference
    if (!discountValue) {
      const widget = document.querySelector('.subscription-widget');
      if (widget) {
        const priceOld = widget.querySelector('.subscription-widget__price-old');
        const priceCurrent = widget.querySelector('.subscription-widget__price-current');
        if (priceOld && priceCurrent) {
          const oldText = priceOld.textContent.replace(/[^\d,]/g, '').replace(',', '.');
          const currentText = priceCurrent.textContent.replace(/[^\d,]/g, '').replace(',', '.');
          const oldPrice = parseFloat(oldText);
          const currentPrice = parseFloat(currentText);
          if (oldPrice > 0 && currentPrice > 0) {
            const discountPercent = Math.round(((oldPrice - currentPrice) / oldPrice) * 100);
            if (discountPercent > 0) {
              discountValue = `${discountPercent}%`;
            }
          }
        }
      }
    }
    
    // Default to 25% if nothing found (common subscription discount)
    if (!discountValue) {
      discountValue = '25%';
    }
    
    // Create or update "Desconto no próximo pedido" property
    if (existingDiscount) {
      existingDiscount.value = discountValue;
    } else {
      const discountInput = document.createElement('input');
      discountInput.type = 'hidden';
      discountInput.name = 'properties[Desconto no próximo pedido]';
      discountInput.value = discountValue;
      form.appendChild(discountInput);
    }
  }
  
  // Force recreate subscription properties from app original
  forceRecreateSubscriptionProperties() {
    if (!this.originalAppElement) {
      return;
    }
    
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) {
      return;
    }
    
    // First, trigger the subscription radio to ensure app is in subscription mode
    const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
    const oneTimeRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
    
    if (subscriptionRadio) {
      // Uncheck one-time first
      if (oneTimeRadio && oneTimeRadio.checked) {
        oneTimeRadio.checked = false;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
        oneTimeRadio.dispatchEvent(new Event('click', { bubbles: true }));
      }
      
      // Then check subscription
      if (!subscriptionRadio.checked) {
        subscriptionRadio.checked = true;
        subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
        subscriptionRadio.dispatchEvent(new Event('click', { bubbles: true }));
      }
    }
    
    // Wait a bit for app to process the radio change
    setTimeout(() => {
      // Trigger the interval select to force property creation
      const intervalSelect = this.originalAppElement.querySelector('select[name="subs_interval"]');
      if (intervalSelect) {
        // Ensure interval matches selected interval
        const valueMap = {
          '30-day': '30 day',
          '45-day': '45 day',
          '60-day': '60 day'
        };
        const targetValue = valueMap[this.selectedInterval] || '30 day';
        
        
        if (intervalSelect.value !== targetValue) {
          intervalSelect.value = targetValue;
        }
        
        // Trigger multiple events to ensure app processes
        intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
        intervalSelect.dispatchEvent(new Event('input', { bubbles: true }));
        intervalSelect.dispatchEvent(new Event('focus', { bubbles: true }));
        intervalSelect.dispatchEvent(new Event('blur', { bubbles: true }));
      } else {
      }
      
      // Wait for app to create inputs, then copy them multiple times
      setTimeout(() => {
        this._doCopyProperties(form);
      }, 150);
      
      setTimeout(() => {
        this._doCopyProperties(form);
      }, 300);
      
      setTimeout(() => {
        this._doCopyProperties(form);
      }, 500);
      
      // Try again after a longer delay
      setTimeout(() => {
        this._doCopyProperties(form);
        
        // Final verification
        const finalProps = form.querySelectorAll('input[name^="properties["][name*="sealsub"]');
      }, 800);
    }, 150);
  }

  setupQuantityListeners(widget) {
    const quantityOptions = widget.querySelectorAll('.subscription-widget__quantity-option');
    quantityOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent toggle of subscription
        const quantity = parseInt(option.dataset.quantity);
        this.selectQuantity(quantity, widget);
      });
    });
  }

  selectOption(optionType, widget) {
    const oneTimeOption = widget.querySelector('.subscription-widget__option[data-option="one-time"]');
    const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
    if (!oneTimeOption || !recurringOption) return;

    // Update selected option state
    this.selectedOption = optionType;

    // Toggle body class for progressive discount: remove orange from default option when subscription is selected
    if (optionType === 'recurring') {
      document.body.classList.add('subscription-recurring-selected');
    } else {
      document.body.classList.remove('subscription-recurring-selected');
    }

    if (optionType === 'one-time') {
      // Remover --selected de todas as opções primeiro (evita ficar os dois selecionados)
      widget.querySelectorAll('.subscription-widget__option').forEach(function(opt) {
        opt.classList.remove('subscription-widget__option--selected');
      });
      widget.querySelectorAll('.subscription-widget__radio').forEach(function(radio) {
        radio.classList.remove('subscription-widget__radio--selected');
      });

      // Selecionar apenas one-time
      oneTimeOption.classList.add('subscription-widget__option--selected');
      const oneTimeRadio = oneTimeOption.querySelector('.subscription-widget__radio');
      if (oneTimeRadio) {
        oneTimeRadio.classList.add('subscription-widget__radio--selected');
      }

      // Re-enable the quantity breaks app (Pumper Bundles)
      this.toggleQuantityBreaksApp(true);

      // Clean up form - remove subscription properties and set quantity to 1
      this.cleanupFormForOneTime();

      // Update app widget to one-time
      if (this.originalAppElement && !this.isUpdatingAppWidget) {
        this.isUpdatingAppWidget = true;
        
        const oneTimeRadioApp = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
        if (oneTimeRadioApp) {
          oneTimeRadioApp.checked = true;
          setTimeout(() => {
            oneTimeRadioApp.dispatchEvent(new Event('change', { bubbles: true }));
            this.isUpdatingAppWidget = false;
          }, 0);
        } else {
          this.isUpdatingAppWidget = false;
        }
      }

      // Set quantity to 1 for one-time purchase (cleanupFormForOneTime already does this, but ensure it's done)
      // cleanupFormForOneTime() already handles this, but we'll verify it here too
      const form = document.querySelector('form[data-type="add-to-cart-form"]');
      if (form) {
        // Double-check quantity is 1
        setTimeout(() => {
          const quantityInput = form.querySelector('input[name="quantity"]');
          if (quantityInput && quantityInput.value !== '1') {
            quantityInput.value = '1';
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }, 50);
      }

      // Update price display
      setTimeout(() => {
        this.updatePriceDisplay(widget);
      }, 100);
    } else if (optionType === 'recurring') {

      // Restaurar produto original do desconto progressivo (voltar ao produto da página)
      const progressiveDiscount = document.querySelector('[data-progressive-discount]');
      const originalOption = progressiveDiscount && progressiveDiscount.querySelector('.progressive-discount__option[data-is-related="false"]');
      if (originalOption && originalOption.dataset.variantId) {
        const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]') || document.querySelector('form.product-form input[name="id"]') || document.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = originalOption.dataset.variantId;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          variantInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        progressiveDiscount.querySelectorAll('.progressive-discount__option').forEach(function(opt) {
          opt.classList.remove('progressive-discount__option--selected');
        });
        originalOption.classList.add('progressive-discount__option--selected');
      }

      // Remover --selected de todas as opções primeiro (evita ficar os dois selecionados)
      widget.querySelectorAll('.subscription-widget__option').forEach(function(opt) {
        opt.classList.remove('subscription-widget__option--selected');
      });
      widget.querySelectorAll('.subscription-widget__radio').forEach(function(radio) {
        radio.classList.remove('subscription-widget__radio--selected');
      });

      // Selecionar apenas recurring
      recurringOption.classList.add('subscription-widget__option--selected');
      const recurringRadio = recurringOption.querySelector('.subscription-widget__radio');
      if (recurringRadio) {
        recurringRadio.classList.add('subscription-widget__radio--selected');
      }

      // Disable the quantity breaks app (Pumper Bundles)
      this.toggleQuantityBreaksApp(false);

      const form = document.querySelector('form[data-type="add-to-cart-form"]');
      
      // IMPORTANT: Remove any existing subscription properties first to force recreation
      if (form) {
        this.removeSubscriptionProperties(form);
      }
      
      // Update app widget to subscription FIRST (so it recreates the property inputs)
      if (this.originalAppElement && !this.isUpdatingAppWidget) {
        this.isUpdatingAppWidget = true;
        
        const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
        const oneTimeRadioApp = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
        
        if (subscriptionRadio) {
          
          // ALWAYS uncheck one-time first (even if not checked, to ensure clean state)
          if (oneTimeRadioApp) {
            if (oneTimeRadioApp.checked) {
            }
            oneTimeRadioApp.checked = false;
            oneTimeRadioApp.dispatchEvent(new Event('change', { bubbles: true }));
            oneTimeRadioApp.dispatchEvent(new Event('click', { bubbles: true }));
          }
          
          // ALWAYS check subscription radio (even if already checked, to force app to regenerate properties)
          subscriptionRadio.checked = true;
          subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
          subscriptionRadio.dispatchEvent(new Event('click', { bubbles: true }));
          
          // Wait for app to process the radio change
          setTimeout(() => {
            
            // Update interval select to match selected interval
            const intervalSelect = this.originalAppElement.querySelector('select[name="subs_interval"]');
            if (intervalSelect) {
              const valueMap = {
                '30-day': '30 day',
                '45-day': '45 day',
                '60-day': '60 day'
              };
              const targetValue = valueMap[this.selectedInterval] || '30 day';
              
              
              // ALWAYS trigger change, even if value is the same, to force app to regenerate properties
              const previousValue = intervalSelect.value;
              intervalSelect.value = targetValue;
              
              // If value changed or we need to force regeneration, trigger events
              if (previousValue !== targetValue || true) { // Always trigger to force regeneration
                intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
                intervalSelect.dispatchEvent(new Event('input', { bubbles: true }));
                intervalSelect.dispatchEvent(new Event('focus', { bubbles: true }));
                intervalSelect.dispatchEvent(new Event('blur', { bubbles: true }));
              }
            }
            
            // Wait a bit more for app to create properties, then force recreate
            setTimeout(() => {
              this.forceRecreateSubscriptionProperties();
              
              // Copy properties multiple times with increasing delays
              if (form) {
                setTimeout(() => {
                  this.copyPropertiesToForm(form);
                  
                  // Verify properties were added
                  const props = form.querySelectorAll('input[name^="properties["][name*="sealsub"]');
                  
                  if (props.length === 0) {
                    // Try again with more aggressive recreation
                    setTimeout(() => {
                      // Force app to regenerate by changing interval value temporarily
                      const intervalSelect = this.originalAppElement.querySelector('select[name="subs_interval"]');
                      if (intervalSelect) {
                        const currentValue = intervalSelect.value;
                        // Temporarily change to different value
                        intervalSelect.value = currentValue === '30 day' ? '45 day' : '30 day';
                        intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
                        
                        // Change back
                        setTimeout(() => {
                          intervalSelect.value = currentValue;
                          intervalSelect.dispatchEvent(new Event('change', { bubbles: true }));
                          
                          setTimeout(() => {
                            this.copyPropertiesToForm(form);
                            this.forceRecreateSubscriptionProperties();
                            
                            setTimeout(() => {
                              this.copyPropertiesToForm(form);
                            }, 200);
                          }, 200);
                        }, 100);
                      }
                    }, 200);
                  }
                }, 200);
                
                setTimeout(() => {
                  this.copyPropertiesToForm(form);
                }, 500);
                
                setTimeout(() => {
                  this.copyPropertiesToForm(form);
                  
                  // Final verification
                  const finalProps = form.querySelectorAll('input[name^="properties["][name*="sealsub"]');
                }, 800);
              }
              
              // Update quantity AFTER properties are added
              setTimeout(() => {
                this.selectQuantity(this.selectedQuantity || 2, widget);
                this.isUpdatingAppWidget = false;
              }, 400);
            }, 250);
          }, 150);
        } else {
          this.isUpdatingAppWidget = false;
          
          // Fallback: try to copy properties anyway
          if (form) {
            this.copyPropertiesToForm(form);
            setTimeout(() => {
              this.forceRecreateSubscriptionProperties();
            }, 100);
          }
          
        this.selectQuantity(this.selectedQuantity || 2, widget);
      }
    } else {
      // Fallback: just update quantity if app element not available
        if (form) {
          this.copyPropertiesToForm(form);
          setTimeout(() => {
            this.forceRecreateSubscriptionProperties();
          }, 100);
        }
        
        this.selectQuantity(this.selectedQuantity || 2, widget);
      }

      // Update price display
      setTimeout(() => {
        this.updatePriceDisplay(widget);
      }, 500);
    }
  }

  toggleQuantityBreaksApp(enabled) {
    // Find the Pumper Bundles / Quantity Breaks app container
    const bundlesWrapper = document.querySelector('.block__cbwrapper');
    if (!bundlesWrapper) return;

    if (enabled) {
      // Enable the quantity breaks app - remove disabled styling
      bundlesWrapper.style.pointerEvents = '';
      bundlesWrapper.style.opacity = '';
      bundlesWrapper.classList.remove('subscription-widget__bundles-disabled');
      
      // Re-enable all radio inputs
      const allRadios = bundlesWrapper.querySelectorAll('input[type="radio"]');
      allRadios.forEach(radio => {
        radio.disabled = false;
      });
    } else {
      // Disable the quantity breaks app visually but keep it visible
      bundlesWrapper.style.pointerEvents = 'none';
      bundlesWrapper.style.opacity = '0.5';
      bundlesWrapper.classList.add('subscription-widget__bundles-disabled');

      // Select the single item option (last one, value="3" - no discount) 
      // This removes the bundle discount
      const singleItemRadio = bundlesWrapper.querySelector('input[type="radio"][value="3"]') || 
                              bundlesWrapper.querySelector('input.prvw__quantity_input3');
      
      if (singleItemRadio) {
        singleItemRadio.checked = true;
        singleItemRadio.dispatchEvent(new Event('change', { bubbles: true }));
        singleItemRadio.dispatchEvent(new Event('click', { bubbles: true }));
        
        // Also click the label to ensure the app updates
        const label = bundlesWrapper.querySelector('label[for="' + singleItemRadio.id + '"]');
        if (label) {
          label.click();
        }
      }

      // Disable all radio inputs to prevent changes while subscription is active
      const allRadios = bundlesWrapper.querySelectorAll('input[type="radio"]');
      allRadios.forEach(radio => {
        radio.disabled = true;
      });
    }
  }

  selectInterval(interval, widget) {
    this.selectedInterval = interval;

    // Auto-select subscription when user interacts with interval options
    const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
    const wasOneTimeSelected = this.selectedOption === 'one-time';
    if (recurringOption && !recurringOption.classList.contains('subscription-widget__option--selected')) {
      this.selectOption('recurring', widget);
      
      // If we just switched from one-time to recurring, ensure properties are added immediately
      if (wasOneTimeSelected) {
        // Wait a bit for selectOption to complete, then force add properties
        setTimeout(() => {
          const form = document.querySelector('form[data-type="add-to-cart-form"]');
          if (form) {
            // Ensure app is in subscription mode
            if (this.originalAppElement) {
              const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
              if (subscriptionRadio && !subscriptionRadio.checked) {
                subscriptionRadio.checked = true;
                subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
            
            // Force recreate subscription properties
            this.forceRecreateSubscriptionProperties();
            
            // Also copy properties multiple times to ensure they're added
            setTimeout(() => {
              this.copyPropertiesToForm(form);
            }, 100);
            setTimeout(() => {
              this.copyPropertiesToForm(form);
            }, 300);
          }
        }, 200);
      }
    }

    // Update UI
    const intervalOptions = widget.querySelectorAll('.subscription-widget__interval-option');
    intervalOptions.forEach(opt => {
      opt.classList.remove('subscription-widget__interval-option--selected');
    });

    const selectedOption = widget.querySelector(`[data-interval="${interval}"]`);
    if (selectedOption) {
      selectedOption.classList.add('subscription-widget__interval-option--selected');
    }

    // Update app select and ensure subscription is selected
    if (this.originalAppElement) {
      // Set flag to prevent infinite loops
      this.isUpdatingAppWidget = true;

      try {
        // Make sure subscription radio is checked
        const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
        const oneTimeRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
        
        // Uncheck one-time if it's checked
        if (oneTimeRadio && oneTimeRadio.checked) {
          oneTimeRadio.checked = false;
          oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        if (subscriptionRadio && !subscriptionRadio.checked) {
          subscriptionRadio.checked = true;
          // Use a timeout to break the event chain
          setTimeout(() => {
            subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
            subscriptionRadio.dispatchEvent(new Event('click', { bubbles: true }));
          }, 0);
        }

        // Update interval select
        const select = this.originalAppElement.querySelector('select[name="subs_interval"]');
        if (select) {
          const valueMap = {
            '30-day': '30 day',
            '45-day': '45 day',
            '60-day': '60 day'
          };
          const newValue = valueMap[interval] || '30 day';
          
          // Force update the select value
          // Try multiple methods to ensure it updates
          if (select.value !== newValue) {
            // Method 1: Direct value assignment
            select.value = newValue;
            
            // Method 2: Set selectedIndex if value didn't work
            const optionIndex = Array.from(select.options).findIndex(opt => opt.value === newValue);
            if (optionIndex !== -1) {
              select.selectedIndex = optionIndex;
            }
            
            // Method 3: Set selected property on option
            Array.from(select.options).forEach(opt => {
              opt.selected = (opt.value === newValue);
            });
            
            // Trigger multiple events to ensure app picks it up
            setTimeout(() => {
              // Trigger change event
              const changeEvent = new Event('change', { bubbles: true, cancelable: true });
              select.dispatchEvent(changeEvent);
              
              // Also trigger input event (some apps listen to this)
              const inputEvent = new Event('input', { bubbles: true, cancelable: true });
              select.dispatchEvent(inputEvent);
              
              // Force a blur/focus cycle
              select.focus();
              select.blur();
              
              this.isUpdatingAppWidget = false;
              
              // Update form properties after interval change - try multiple times with delays
              const form = document.querySelector('form[data-type="add-to-cart-form"]');
              if (form) {
                // Try immediately
                this.copyPropertiesToForm(form);
                
                // Force recreate properties to ensure they're added
                setTimeout(() => {
                  this.forceRecreateSubscriptionProperties();
                }, 50);
                
                // Try after short delay
                setTimeout(() => {
                  this.copyPropertiesToForm(form);
                }, 100);
                
                // Try after longer delay
                setTimeout(() => {
                  this.copyPropertiesToForm(form);
                  this.forceRecreateSubscriptionProperties();
                  // Update price display after discount changes
                  this.updatePriceDisplay(widget);
                }, 300);
              }
            }, 10);
          } else {
            this.isUpdatingAppWidget = false;
            
            // Still update form to ensure properties are current
            const form = document.querySelector('form[data-type="add-to-cart-form"]');
            if (form) {
              this.copyPropertiesToForm(form);
              // Force recreate properties
              setTimeout(() => {
                this.forceRecreateSubscriptionProperties();
              }, 100);
              // Update price display
              this.updatePriceDisplay(widget);
            }
          }
        } else {
          this.isUpdatingAppWidget = false;
        }
      } catch (e) {
        this.isUpdatingAppWidget = false;
      }
    }
  }

  async selectQuantity(quantity, widget) {
    this.selectedQuantity = quantity;

    // Auto-select subscription when user interacts with quantity options
    const recurringOption = widget.querySelector('.subscription-widget__option--recurring');
    const wasOneTimeSelected = this.selectedOption === 'one-time';
    if (recurringOption && !recurringOption.classList.contains('subscription-widget__option--selected')) {
      this.selectOption('recurring', widget);
      
      // If we just switched from one-time to recurring, ensure properties are added immediately
      if (wasOneTimeSelected) {
        // Wait a bit for selectOption to complete, then force add properties
        setTimeout(() => {
          const form = document.querySelector('form[data-type="add-to-cart-form"]');
          if (form) {
            // Ensure app is in subscription mode
            if (this.originalAppElement) {
              const subscriptionRadio = this.originalAppElement.querySelector('input[name^="subs_type_"][value="subscription"]');
              if (subscriptionRadio && !subscriptionRadio.checked) {
                subscriptionRadio.checked = true;
                subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
            
            // Force recreate subscription properties
            this.forceRecreateSubscriptionProperties();
            
            // Also copy properties multiple times to ensure they're added
            setTimeout(() => {
              this.copyPropertiesToForm(form);
            }, 100);
            setTimeout(() => {
              this.copyPropertiesToForm(form);
            }, 300);
          }
        }, 200);
      }
    }

    // Update UI
    const quantityOptions = widget.querySelectorAll('.subscription-widget__quantity-option');
    quantityOptions.forEach(opt => {
      opt.classList.remove('subscription-widget__quantity-option--selected');
    });

    const selectedOption = widget.querySelector(`[data-quantity="${quantity}"]`);
    if (!selectedOption) {
      return;
    }
    
    selectedOption.classList.add('subscription-widget__quantity-option--selected');

    // Update quantity in the form (same product, just different quantity)
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) {
      return;
    }

    // Find or create quantity input as hidden
    let quantityInput = form.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      // Create if doesn't exist as hidden input
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.setAttribute('value', quantity);
      quantityInput.value = quantity;
      form.appendChild(quantityInput);
    } else {
      // Update existing quantity input and make sure it's hidden
      quantityInput.type = 'hidden';
      quantityInput.setAttribute('value', quantity);
      quantityInput.value = quantity;
      // Also set the property directly to ensure it sticks
      Object.defineProperty(quantityInput, 'value', {
        value: String(quantity),
        writable: true,
        configurable: true
      });
    }

    // Also check for quantity-input custom element and update it
    const quantityInputElement = form.querySelector('quantity-input input');
    if (quantityInputElement) {
      quantityInputElement.setAttribute('value', quantity);
      quantityInputElement.value = quantity;
      quantityInputElement.type = 'hidden';
      quantityInputElement.dispatchEvent(new Event('change', { bubbles: true }));
      quantityInputElement.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Hide the quantity-input custom element container
    const quantityInputContainer = form.querySelector('quantity-input');
    if (quantityInputContainer) {
      quantityInputContainer.style.display = 'none';
    }

    // Hide the product-form__quantity container
    const quantityContainer = form.querySelector('.product-form__quantity');
    if (quantityContainer) {
      quantityContainer.style.display = 'none';
    }

    // Trigger change event
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    
    // Also update all quantity inputs in all forms to ensure consistency
    const allQuantityInputs = document.querySelectorAll('input[name="quantity"]');
    allQuantityInputs.forEach(input => {
      if (input !== quantityInput) {
        input.setAttribute('value', quantity);
        input.value = quantity;
      }
    });

    // Trigger change events on form to notify app widget
    if (form) {
      form.dispatchEvent(new Event('change', { bubbles: true }));
      form.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Ensure subscription properties are added when subscription is selected
    if (this.selectedOption === 'recurring') {
      // Copy properties to form
      this.copyPropertiesToForm(form);
      
      // Also try to force recreate if needed
      setTimeout(() => {
        this.forceRecreateSubscriptionProperties();
      }, 100);
    }

    // Update price display with new quantity - wait a bit for app to recalculate discount
    if (widget) {
      // Update immediately
      this.updatePriceDisplay(widget);
      
      // Update again after short delay to catch app's discount recalculation
      setTimeout(() => {
        this.updatePriceDisplay(widget);
      }, 200);
      
      // Update again after longer delay to ensure app has updated
      setTimeout(() => {
        this.updatePriceDisplay(widget);
      }, 500);
    }
  }

  async updateFormWithIds(productId, variantId) {
    try {
      // Validate inputs
      if (!productId || !variantId) {
        return;
      }
      
      // Ensure variantId is not the same as productId (common mistake)
      if (String(variantId) === String(productId)) {
        // Try to load product to get variant
        const product = this.relatedProducts.find(p => (p.id || p) === productId);
        if (product) {
          const productData = await this.loadProductData(product);
          if (productData && productData.variants && productData.variants.length > 0) {
            const variant = productData.variants.find(v => v.available) || productData.variants[0];
            variantId = variant.id;
          } else {
            return;
          }
        } else {
          return;
        }
      }
      
      // Get the form
      const form = document.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) {
        return;
      }
      
      // Update variant ID (input name="id") - THIS IS THE VARIANT ID, NOT PRODUCT ID
      let variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) {
        // Create if doesn't exist
        variantInput = document.createElement('input');
        variantInput.type = 'hidden';
        variantInput.name = 'id';
        form.appendChild(variantInput);
      }
      
      // Remove disabled attribute if present
      if (variantInput.hasAttribute('disabled')) {
        variantInput.removeAttribute('disabled');
      }
      
      variantInput.value = variantId;
      variantInput.setAttribute('value', variantId);
      
      // Update product ID (input name="product-id")
      let productIdInput = form.querySelector('input[name="product-id"]');
      if (!productIdInput) {
        // Create if doesn't exist
        productIdInput = document.createElement('input');
        productIdInput.type = 'hidden';
        productIdInput.name = 'product-id';
        form.appendChild(productIdInput);
      }
      
      // Remove disabled attribute if present
      if (productIdInput.hasAttribute('disabled')) {
        productIdInput.removeAttribute('disabled');
      }
      
      productIdInput.value = productId;
      productIdInput.setAttribute('value', productId);
      
      // Update all variant inputs on the page
      const allVariantInputs = document.querySelectorAll('input[name="id"]');
      allVariantInputs.forEach(input => {
        if (input.value !== String(variantId)) {
          if (input.hasAttribute('disabled')) {
            input.removeAttribute('disabled');
          }
          input.value = variantId;
          input.setAttribute('value', variantId);
        }
      });
      
      // Update all product-id inputs on the page
      const allProductIdInputs = document.querySelectorAll('input[name="product-id"]');
      allProductIdInputs.forEach(input => {
        if (input.value !== String(productId)) {
          if (input.hasAttribute('disabled')) {
            input.removeAttribute('disabled');
          }
          input.value = productId;
          input.setAttribute('value', productId);
        }
      });
      
      // Trigger events
      const events = ['change', 'input'];
      events.forEach(eventType => {
        const event = new Event(eventType, { bubbles: true, cancelable: true });
        variantInput.dispatchEvent(event);
        productIdInput.dispatchEvent(event);
      });
      
      // Update current IDs
      this.currentVariantId = variantId;
      this.currentProductId = productId;
      
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('subscription:product-changed', {
        detail: {
          productId: productId,
          variantId: variantId
        }
      }));
      
    } catch (e) {
    }
  }

  async updateFormWithProduct(productData) {
    try {
      if (!productData || !productData.variants || productData.variants.length === 0) {
        return;
      }

      // Find the first available variant or first variant
      let selectedVariant = productData.variants.find(v => v.available) || productData.variants[0];
      
      // Get the form
      const form = document.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) {
        return;
      }
      
      // Update variant ID in the form - try multiple selectors
      let variantInput = form.querySelector('input[name="id"]');
      
      // If not found, try to find it in product-form custom element
      if (!variantInput) {
        const productForm = form.closest('product-form') || document.querySelector('product-form');
        if (productForm) {
          variantInput = productForm.querySelector('input[name="id"]');
        }
      }
      
      // If still not found, try to find any input with name="id" in the form
      if (!variantInput) {
        variantInput = form.querySelector('input[name="id"]') || form.querySelector('[name="id"]');
      }
      
      if (variantInput) {
        // Remove disabled attribute if present (Shopify sometimes disables this field)
        if (variantInput.hasAttribute('disabled')) {
          variantInput.removeAttribute('disabled');
        }
        
        // Update the value using multiple methods to ensure it sticks
        variantInput.value = selectedVariant.id;
        variantInput.setAttribute('value', selectedVariant.id);
        
        // Also set the property directly (for some frameworks)
        Object.defineProperty(variantInput, 'value', {
          value: String(selectedVariant.id),
          writable: true,
          configurable: true
        });
        
        // Also update any hidden variant inputs in the form
        const hiddenVariantInputs = form.querySelectorAll('input[type="hidden"][name="id"]');
        hiddenVariantInputs.forEach(input => {
          if (input.value !== String(selectedVariant.id)) {
            if (input.hasAttribute('disabled')) {
              input.removeAttribute('disabled');
            }
            input.value = selectedVariant.id;
            input.setAttribute('value', selectedVariant.id);
          }
        });
        
        // Trigger multiple events to ensure all listeners are notified
        const events = ['change', 'input', 'blur'];
        events.forEach(eventType => {
          const event = new Event(eventType, { bubbles: true, cancelable: true });
          variantInput.dispatchEvent(event);
        });
      } else {
        // Create the input if it doesn't exist
        const newInput = document.createElement('input');
        newInput.type = 'hidden';
        newInput.name = 'id';
        newInput.value = selectedVariant.id;
        form.appendChild(newInput);
      }

      // Update variant ID in ALL forms on the page (if multiple exist)
      const allVariantInputs = document.querySelectorAll('input[name="id"]');
      allVariantInputs.forEach(input => {
        if (input.value !== String(selectedVariant.id)) {
          // Remove disabled attribute if present
          if (input.hasAttribute('disabled')) {
            input.removeAttribute('disabled');
          }
          
          // Update value
          input.value = selectedVariant.id;
          input.setAttribute('value', selectedVariant.id);
          
          // Trigger events
          input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
          input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        }
      });
      
      // Also try to update any select elements with name="id"
      const variantSelectElements = document.querySelectorAll('select[name="id"]');
      variantSelectElements.forEach(select => {
        if (select.value !== String(selectedVariant.id)) {
          select.value = selectedVariant.id;
          select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        }
      });

      // Update current IDs
      this.currentVariantId = selectedVariant.id;
      this.currentProductId = productData.id;

      // Update variant selectors if they exist
      const variantSelectors = document.querySelectorAll('variant-selects, variant-radios');
      variantSelectors.forEach(selector => {
        if (selector.updateOptions) {
          selector.updateOptions();
        }
        if (selector.updateVariant) {
          selector.updateVariant(selectedVariant.id);
        }
        // Try to trigger change on the selector itself
        selector.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variantId: selectedVariant.id }
        }));
      });

      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('subscription:product-changed', {
        detail: {
          productId: productData.id,
          variantId: selectedVariant.id,
          product: productData
        }
      }));
    } catch (e) {
    }
  }

  // Functions removed - the app original handles adding properties automatically
  // We just sync the selections (radio, select, quantity) and let the app do its work
}

// Restaura o variant selecionado no progressive no form (para não perder ao forçar one-time)
function restoreProgressiveVariantInForm() {
  var option = document.querySelector('[data-progressive-discount] .progressive-discount__option--selected');
  if (!option || !option.dataset.variantId) return;
  var container = document.querySelector('[data-progressive-discount]');
  var form = container && container.closest('form[action*="/cart/add"]');
  var variantInput = form ? form.querySelector('input[name="id"]') : (document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') || document.querySelector('input[name="id"]'));
  if (variantInput) {
    variantInput.removeAttribute('disabled');
    if (variantInput.value !== option.dataset.variantId) {
      variantInput.value = option.dataset.variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

// Listener global: ao clicar em opção do progressive com assinatura selecionada, voltar para Comprar uma vez
function forceSubscriptionWidgetToOneTime() {
  var widget = document.querySelector('.subscription-widget');
  if (!widget) return;
  var oneTimeOption = widget.querySelector('.subscription-widget__option[data-option="one-time"]');
  var recurringOption = widget.querySelector('.subscription-widget__option--recurring');
  if (!oneTimeOption || !recurringOption) return;

  // Sempre atualizar DOM do widget primeiro (só uma opção selecionada)
  widget.querySelectorAll('.subscription-widget__option').forEach(function(opt) {
    opt.classList.remove('subscription-widget__option--selected');
  });
  widget.querySelectorAll('.subscription-widget__radio').forEach(function(r) {
    r.classList.remove('subscription-widget__radio--selected');
  });
  oneTimeOption.classList.add('subscription-widget__option--selected');
  var rt = oneTimeOption.querySelector('.subscription-widget__radio');
  if (rt) rt.classList.add('subscription-widget__radio--selected');
  document.body.classList.remove('subscription-recurring-selected');

  var instance = widget._subscriptionWidgetInstance;
  if (instance && typeof instance.selectOption === 'function') {
    instance.selectedOption = 'one-time';
    // Não chamar cleanupFormForOneTime: o progressive setou variant; não sobrescrever
    if (instance.originalAppElement) {
      instance.isUpdatingAppWidget = true;
      var oneTimeRadioApp = instance.originalAppElement.querySelector('input[name^="subs_type_"][value="one_time"]');
      if (oneTimeRadioApp) {
        oneTimeRadioApp.checked = true;
        oneTimeRadioApp.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setTimeout(function() { instance.isUpdatingAppWidget = false; }, 50);
    }
  } else {
    document.querySelectorAll('input[name^="subs_type_"][value="one_time"]').forEach(function(radio) {
      if (!radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // Garantir que o variant do progressive permanece no form (app pode ter sobrescrito)
  setTimeout(restoreProgressiveVariantInForm, 50);

  // Comprar uma vez = quantidade 1
  var form = document.querySelector('[data-progressive-discount]') && document.querySelector('[data-progressive-discount]').closest('form[action*="/cart/add"]');
  if (!form) form = document.querySelector('form[data-type="add-to-cart-form"]');
  if (form) {
    form.querySelectorAll('input[name="quantity"]').forEach(function(input) {
      input.value = '1';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    var qtyInput = form.querySelector('quantity-input input');
    if (qtyInput) { qtyInput.value = '1'; qtyInput.dispatchEvent(new Event('change', { bubbles: true })); }
  }
}

document.addEventListener('subscription-widget:switch-to-one-time', function() {
  // Executar com pequeno atraso e uma vez depois (evitar muitas execuções que atrapalham variant do progressive)
  setTimeout(forceSubscriptionWidgetToOneTime, 0);
  setTimeout(forceSubscriptionWidgetToOneTime, 200);
});

// Initialize when DOM is ready
let widgetInitialized = false;

function initSubscriptionWidget() {
  if (widgetInitialized) {
    return;
  }
  
  const customWidget = document.querySelector('.subscription-widget');
  if (customWidget) {
    widgetInitialized = true;
    return;
  }
  
  try {
    new SubscriptionWidget();
    widgetInitialized = true;
  } catch (e) {
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubscriptionWidget);
} else {
  initSubscriptionWidget();
}

// Also try after window load in case app loads late
window.addEventListener('load', () => {
  setTimeout(() => {
    if (!document.querySelector('.subscription-widget')) {
      widgetInitialized = false;
      initSubscriptionWidget();
    }
  }, 1000);
});

// Fallback: check periodically for app element
let checkCount = 0;
const maxChecks = 10;
const checkInterval = setInterval(() => {
  checkCount++;
  const appElement = document.querySelector('select.sls-select.seal-tight[name="subs_interval"]');
  const customWidget = document.querySelector('.subscription-widget');
  
  if (appElement && !customWidget && checkCount < maxChecks && !widgetInitialized) {
    widgetInitialized = false;
    initSubscriptionWidget();
  } else if (checkCount >= maxChecks || customWidget) {
    clearInterval(checkInterval);
  }
}, 500);


